import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authApi } from '../services/api/auth';
import { canUserAccessNavigationPath } from '../utils/navigationAccess';

// Set immediately before a full reload so an in-flight profile check cannot
// write the previous account back into localStorage.
let authSessionReloadPending = false;
let sessionEpoch = 0;

function reloadAsSignedInUser(nextUser) {
  authSessionReloadPending = true;
  const pathname = window.location.pathname || '/';
  const search = window.location.search || '';
  const hash = window.location.hash || '';
  const stay = canUserAccessNavigationPath(nextUser, pathname);
  window.location.assign(stay ? `${pathname}${search}${hash}` : '/');
}

const PROFILE_POLL_MS = 5 * 60 * 1000;

function normalizePermissionsPayload(payload = {}) {
  return {
    roles: payload.roles ?? payload.Roles ?? [],
    companyAssignments: payload.companyAssignments ?? payload.CompanyAssignments ?? [],
    raciAssignments: payload.raciAssignments ?? payload.RaciAssignments ?? [],
    activeModules: payload.activeModules ?? payload.ActiveModules ?? [],
    activeModuleCodes: payload.activeModuleCodes ?? payload.ActiveModuleCodes ?? [],
    debug: payload._debug
  };
}

function clearStoredSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('auth-session');
}

function markAuthSession(userId) {
  localStorage.setItem('auth-session', String(userId || Date.now()));
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    const parsedUser = savedUser ? JSON.parse(savedUser) : null;
    return parsedUser;
  });
  const [loading, setLoading] = useState(true);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  // Confirm the session cookie and load the profile. The cookie is HttpOnly, so the
  // browser cannot read the token; a failed profile request means signed out.
  useEffect(() => {
    const checkAuth = async () => {
      if (authSessionReloadPending) return;
      const epoch = sessionEpoch;
      try {
        const response = await authApi.getProfile();
        if (epoch !== sessionEpoch) return;
        const userData = response.data || response;
        localStorage.removeItem('token');
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        setLoading(false);
      } catch (error) {
        if (epoch !== sessionEpoch) return;
        logout();
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // The cookie expires on the server. A rejected session check signs the user out.
  useEffect(() => {
    if (!user) return undefined;

    let interval = null;
    const poll = () => {
      authApi.getProfile({ fresh: true }).catch(() => {});
    };
    const startInterval = () => {
      if (interval) return;
      interval = setInterval(poll, PROFILE_POLL_MS);
    };
    const stop = () => {
      if (!interval) return;
      clearInterval(interval);
      interval = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stop();
        return;
      }
      poll();
      startInterval();
    };

    if (document.visibilityState !== 'hidden') {
      startInterval();
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user]);

  // Reload other tabs when this tab signs in, impersonates, or signs out.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === 'auth-session' || event.key === 'token') {
        window.location.reload();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = useCallback(async (email, password, options = {}) => {
    if (!options.keepPage) setLoading(true);
    sessionEpoch += 1;
    
    try {
      const response = await authApi.login(email, password);
      
      // Extract user and token from the nested response structure
      const { user: userData, requiresPasswordChange } = response.data;

      localStorage.removeItem('token');
      if (requiresPasswordChange) {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true, requiresPasswordChange: true };
      }

      markAuthSession(userData?.id);
      
      // Fetch user permissions
      try {
        setPermissionsLoading(true);
        const permissionsResponse = await authApi.getPermissions();
        
        if (permissionsResponse.success) {
          const normalizedPermissions = normalizePermissionsPayload(permissionsResponse.data);
          const { roles, companyAssignments, raciAssignments, activeModules, activeModuleCodes, debug } = normalizedPermissions;
          if (debug) {
            console.log('[permissions] (login)', debug);
          }
          const formTypeIds = (activeModules || []).map(m => m.FormTypeID ?? m.formTypeID).filter(Boolean);
          console.log('AuthContext - Permissions loaded (login): activeModules count=', (activeModules || []).length, 'FormTypeIDs=', formTypeIds, 'activeModuleCodes=', activeModuleCodes);
          const userWithPermissions = {
            ...userData,
            roles: roles,
            companyAssignments: companyAssignments,
            raciAssignments: raciAssignments || [],
            activeModules: activeModules || [],
            activeModuleCodes: activeModuleCodes || []
          };
          setUser(userWithPermissions);
          localStorage.setItem('user', JSON.stringify(userWithPermissions));
        } else {
          console.warn('AuthContext - Failed to fetch permissions, using basic user data');
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        }
      } catch (err) {
        console.error('AuthContext - Failed to fetch permissions:', err);
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } finally {
        setPermissionsLoading(false);
      }
      
      return { 
        success: true, 
        requiresPasswordChange: requiresPasswordChange || false 
      };
    } catch (error) {
      console.error('AuthContext - Login error:', error);
      return { 
        success: false, 
        message: error.message || 'Login failed. Please check your credentials.' 
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    sessionEpoch += 1;
    setUser(null);
    clearStoredSession();
    authApi.logout().catch(() => {});
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    try {
      const response = await authApi.updateProfile(profileData);
      // Extract user data from the nested response structure
      const userData = response.data || response;
      setUser(userData);
      return { success: true };
    } catch (error) {
      console.error('AuthContext - Profile update error:', error);
      return { 
        success: false, 
        message: error.message || 'Profile update failed' 
      };
    }
  }, []);

  const updateUserPreferences = useCallback(async (preferences) => {
    try {
      const response = await authApi.updatePreferences(preferences);
      
      // Update the user state with the new preferences
      if (response && response.data) {
        // Map database field names to frontend field names
        const mappedData = {
          weeklyDigest: response.data.WeeklyDigest,
          lastLogin: response.data.LastLogin,
          phoneNumber: response.data.PhoneNumber,
          defaultCompany: response.data.DefaultCompany,
          optOutEmails: !!(response.data.OptOutEmails)
        };
        
        setUser((current) => {
          const updatedUser = {
            ...current,
            ...mappedData
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          return updatedUser;
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error('AuthContext - Preferences update error:', error);
      return { 
        success: false, 
        message: error.message || 'Preferences update failed' 
      };
    }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      await authApi.changePassword(currentPassword, newPassword);
      return { success: true };
    } catch (error) {
      console.error('AuthContext - Password change error:', error);
      return { 
        success: false, 
        message: error.message || 'Password change failed' 
      };
    }
  }, []);

  const impersonateUser = useCallback(async (targetUserId) => {
    setLoading(true);
    
    try {
      const response = await authApi.impersonateUser(targetUserId);
      
      // Extract user and token from the nested response structure
      const { user: userData } = response.data;

      localStorage.removeItem('token');
      markAuthSession(userData?.id);
      
      // The backend already returned the impersonated user's permissions
      // in the userData object, so we don't need to fetch them again
      const userWithImpersonationFlag = {
        ...userData,
        isImpersonated: true
      };
      const formTypeIds = (userWithImpersonationFlag.activeModules || [])
        .map((m) => m.FormTypeID ?? m.formTypeID)
        .filter(Boolean);
      console.log(
        'AuthContext - Impersonation permissions: roles=',
        (userWithImpersonationFlag.roles || []).map((r) => r.roleName ?? r),
        'companies=',
        (userWithImpersonationFlag.companyAssignments || []).length,
        'activeModules count=',
        (userWithImpersonationFlag.activeModules || []).length,
        'FormTypeIDs=',
        formTypeIds,
        'activeModuleCodes=',
        userWithImpersonationFlag.activeModuleCodes || []
      );
      setUser(userWithImpersonationFlag);
      localStorage.setItem('user', JSON.stringify(userWithImpersonationFlag));

      // Drop every list, form, and menu loaded as the admin. The next load
      // uses this token, so the screen matches the impersonated user.
      reloadAsSignedInUser(userWithImpersonationFlag);

      return { success: true };
    } catch (error) {
      console.error('AuthContext - Impersonation error:', error);
      return { 
        success: false, 
        message: error.message || 'Impersonation failed.' 
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const stopImpersonation = useCallback(async () => {
    setLoading(true);

    try {
      const response = await authApi.stopImpersonation();

      const { user: responseUser } = response.data || {};
      if (!responseUser) {
        throw new Error('Invalid stop-impersonation response');
      }

      localStorage.removeItem('token');
      markAuthSession(responseUser.id);

      // Use the stop-impersonation response as source of truth (backend returns full roles, companyAssignments, raciAssignments, activeModules)
      const restoredUser = {
        ...responseUser,
        isImpersonated: false
      };
      setUser(restoredUser);
      localStorage.setItem('user', JSON.stringify(restoredUser));

      reloadAsSignedInUser(restoredUser);

      return { success: true };
    } catch (error) {
      console.error('AuthContext - Stop impersonation error:', error);
      if (error.response?.status === 403) {
        sessionEpoch += 1;
        setUser(null);
        clearStoredSession();
        window.location.assign('/login');
      }
      return {
        success: false,
        message: error.message || 'Failed to stop impersonation.'
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUserPermissions = useCallback(async () => {
    try {
      setPermissionsLoading(true);
      const permissionsResponse = await authApi.refreshPermissions();
      
      if (permissionsResponse.success) {
        const normalizedPermissions = normalizePermissionsPayload(permissionsResponse.data);
        const { roles, companyAssignments, raciAssignments, activeModules, activeModuleCodes, debug } = normalizedPermissions;
        if (debug) {
          console.log('[permissions] (refresh)', debug);
        }
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        
        const userWithUpdatedPermissions = {
          ...currentUser,
          roles: roles,
          companyAssignments: companyAssignments,
          raciAssignments: raciAssignments || [],
          activeModules: activeModules || [],
          activeModuleCodes: activeModuleCodes || []
        };
        
        setUser(userWithUpdatedPermissions);
        localStorage.setItem('user', JSON.stringify(userWithUpdatedPermissions));
        
        return userWithUpdatedPermissions;
      } else {
        console.warn('AuthContext - Failed to refresh permissions');
        return null;
      }
    } catch (error) {
      console.error('AuthContext - Error refreshing permissions:', error);
      return null;
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  const permissionsReady = !loading && !permissionsLoading;

  const value = useMemo(() => ({
    user,
    token: null,
    loading,
    permissionsLoading,
    permissionsReady,
    login,
    logout,
    updateProfile,
    updateUserPreferences,
    changePassword,
    impersonateUser,
    stopImpersonation,
    refreshUserPermissions,
    isAuthenticated: !!user
  }), [
    user,
    loading,
    permissionsLoading,
    permissionsReady,
    login,
    logout,
    updateProfile,
    updateUserPreferences,
    changePassword,
    impersonateUser,
    stopImpersonation,
    refreshUserPermissions
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 